import { FC, useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { isNil } from 'ramda';

import Button from 'components/_core/Button';
import Card from 'components/_core/Card';
import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';
import Text from 'components/_core/Text';
import { getEligibility } from 'features/Onboarding/common/localStorage/eligibility';
import BooleanRadioGroup from 'features/Onboarding/components/BooleanRadioGroup';
import { ApplicationType } from 'features/Onboarding/types';

import getRoleLabel from '../../getRoleLabel';
import { ManageElement } from '../../styles';
import ManageShares from '../ManageShares';
import locales from './locales';
import { ManageCurrentUserElementComponentProps } from './types';

const ManageCurrentUserElementComponent: FC<
  ManageCurrentUserElementComponentProps
> = ({
  applicationType,
  getIsAddedAmountSharesValid,
  includeShares,
  isEditing,
  isExistingCompany,
  loading,
  onCardClicked,
  onClose,
  onSave,
  sharesData,
  user,
}) => {
  const isCapitalDeposit = applicationType === ApplicationType.CapitalDeposit;
  const tense = isExistingCompany ? 'present' : 'future';
  const eligibility = getEligibility();

  /**
   * Capital Deposit users have already been asked if they were directors
   * (and they had to answer `yes` to continue).
   */
  const shouldAskIfDirector =
    isCapitalDeposit === false && isNil(user?.isDirector);

  const [nbShares, setNbShares] = useState<number | null>(
    user?.numberOfShares ? user?.numberOfShares : null,
  );
  const [isDirector, setIsDirector] = useState<boolean | null>(
    isCapitalDeposit || (user?.isDirector ?? null),
  );
  const [isShareholder, setIsShareholder] = useState<boolean | null>(
    !isNil(user?.numberOfShares)
      ? (user?.numberOfShares as number) > 0
      : eligibility?.isMajorShareholder ?? null,
  );

  const isGlobalAmountValid =
    !includeShares ||
    getIsAddedAmountSharesValid(nbShares, user?.companyLegalBeneficiaryId);

  const canBeSubmitted =
    isDirector &&
    !isNil(isShareholder) &&
    (!isShareholder || (Boolean(nbShares) && isGlobalAmountValid));

  const percentOfShares =
    nbShares && sharesData?.legalNumberOfShares
      ? (nbShares / sharesData.legalNumberOfShares) * 100
      : 0;

  const [isMajorShareholder, setIsMajorShareholder] = useState<boolean | null>(
    () => {
      if (includeShares) {
        return percentOfShares > 25;
      }

      return (
        (!isNil(user?.isMajorShareholder)
          ? user?.isMajorShareholder
          : eligibility?.isMajorShareholder) ?? null
      );
    },
  );

  useEffect(() => {
    if (includeShares) {
      setIsMajorShareholder(percentOfShares > 25);
    }
  }, [includeShares, percentOfShares]);

  return (
    <ManageElement.Wrapper
      onValidSubmit={() => {
        if (loading || isDirector === null || isMajorShareholder === null) {
          return;
        }

        onSave({
          isDirector,
          isMajorShareholder,
          nbShares,
        });
      }}
    >
      <Card onClick={onCardClicked}>
        <Flex alignItems="stretch" direction="column">
          {!isEditing ? (
            <>
              <Text
                fontWeight="weight-500"
                variant={includeShares ? 'primary' : 'disabled'}
              >
                {`${user?.firstName} ${user?.lastName} (${locales.you})`}
              </Text>

              {user ? (
                <ManageElement.RoleContainer>
                  {getRoleLabel({
                    isDirector,
                    isMajorShareholder,
                    percentOfShares,
                  })}
                </ManageElement.RoleContainer>
              ) : null}

              {includeShares ? (
                <Button
                  alignSelf="start"
                  marginTop="space-12"
                  variant="inline-primary"
                >
                  Modifier
                </Button>
              ) : null}
            </>
          ) : (
            <>
              <ManageElement.CardHeader>
                <Text fontWeight="weight-500" variant="primary">
                  {locales.aboutYou}
                </Text>
                <IconButton
                  aria-label="close"
                  icon="cross"
                  onClick={onClose}
                  size="sm"
                  variant="tertiary"
                />
              </ManageElement.CardHeader>
              {shouldAskIfDirector ? (
                <>
                  <ManageElement.BusinessRoleLabel>
                    {locales.directorLabel[tense]}
                  </ManageElement.BusinessRoleLabel>
                  <ManageElement.BusinessRoleDescription>
                    {locales.directorDescription[tense]}
                  </ManageElement.BusinessRoleDescription>
                  <Form.Field marginBottom="space-24">
                    <BooleanRadioGroup
                      onChange={(value) => {
                        setIsDirector(value);
                      }}
                      value={isDirector}
                    />
                  </Form.Field>
                </>
              ) : null}
              <ManageElement.BusinessRoleLabel>
                {locales.shareholderLabel[tense]}
              </ManageElement.BusinessRoleLabel>
              <ManageElement.BusinessRoleDescription>
                {locales.shareholderDescription}
              </ManageElement.BusinessRoleDescription>
              <Form.Field marginBottom="space-24">
                <BooleanRadioGroup
                  onChange={(value) => {
                    setIsShareholder(value);

                    if (!value) {
                      setNbShares(0);
                    } else {
                      setNbShares(null);
                    }
                  }}
                  value={isShareholder}
                />
              </Form.Field>
              {isShareholder ? (
                <ManageShares
                  individualNumOfShares={nbShares}
                  isGlobalAmountValid={isGlobalAmountValid}
                  onSharesChange={(value) => {
                    setNbShares(value);
                  }}
                  sharesData={sharesData}
                />
              ) : null}
            </>
          )}
        </Flex>
        {isEditing ? (
          <Box marginTop="space-16">
            <Button
              icon="arrow-right"
              isDisabled={!canBeSubmitted || loading}
              isLoading={loading}
              type="submit"
              width="100%"
            >
              {locales.confirm}
            </Button>
          </Box>
        ) : null}
      </Card>
    </ManageElement.Wrapper>
  );
};

export default ManageCurrentUserElementComponent;
